/**
 * CaptionDisplay
 * 句読点で分割してキュー表示対応版（項目13）
 * 画面幅・フォントサイズから動的に文字数上限を計算
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { CaptionState, OBSStyleMode, TranslationDirection } from '../types';
import { DIRECTION_LABELS, TARGET_LABELS } from '../types';

interface Props {
  caption: CaptionState | null;
  fontSize: number;
  showOriginal: boolean;
  obsStyle: OBSStyleMode;
  captionPosition?: 'bottom' | 'top' | 'center';
  // true：届いた字幕をすぐ表示する（順番待ちをしない。会議用の遅れ対策）
  live?: boolean;
  // 即時表示の行数（省略時4＝会議用・スマホ用の従来どおり。セミナー用 live=1 は2）
  liveLines?: 2 | 4;
}

interface QueueItem {
  text: string;
  original: string;
  direction: TranslationDirection;
  target?: string;
}

const OBS_CLASS_MAP: Record<OBSStyleMode, string> = {
  'white-black': '',
  'transparent-black': 'obs-transparent-black',
  'transparent-white': 'obs-transparent-white',
};

// 画面幅とフォントサイズから2行分の最大文字数を計算
function calcMaxChars(fontSize: number, isJapanese: boolean): number {
  const availableWidth = window.innerWidth * 0.65;
  const charWidth = isJapanese ? fontSize * 1.0 : fontSize * 0.55;
  const charsPerLine = Math.floor(availableWidth / charWidth);
  return Math.max(Math.floor(charsPerLine * 1.7), isJapanese ? 20 : 40); // 2行分
}

// 文字数に応じた表示時間（ms）
function calcDisplayDuration(text: string): number {
  const len = text.length;
  if (len < 15) return 4000;
  if (len < 30) return 6000;
  if (len < 45) return 8000;
  return 10000;
}

// 確定字幕を句読点で分割
function splitSentences(text: string, maxChars: number): string[] {
  if (!text.trim()) return [];
  const isJapanese = /[぀-鿿가-힯]/.test(text);

  let parts: string[];
  if (isJapanese) {
    parts = text.split(/(?<=[。！？])|(?<=[.!?])\s+/).map(s => s.trim()).filter(s => s.length > 1);
  } else {
    parts = text.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(s => s.length > 1);
  }

  const sentences = parts.length > 0 ? parts : [text.trim()];

  // 1文がmaxCharsを超える場合はさらに分割
  const result: string[] = [];
  for (const s of sentences) {
    if (s.length <= maxChars) {
      result.push(s);
    } else {
      // maxChars ごとに強制分割（単語境界優先）
      let remaining = s;
      while (remaining.length > maxChars) {
        const chunk = remaining.slice(0, maxChars);
        const lastSpace = isJapanese ? -1 : chunk.lastIndexOf(' ');
        const cutAt = lastSpace > maxChars * 0.6 ? lastSpace : maxChars;
        result.push(remaining.slice(0, cutAt).trim());
        remaining = remaining.slice(cutAt).trim();
      }
      if (remaining.length > 0) result.push(remaining);
    }
  }
  return result;
}

// 中間字幕の最新部分を切り出す（句読点でリセット + 動的文字数制限）
function getLatestPortion(text: string, maxChars: number, natural = false): string {
  if (!text) return text;
  const isJapanese = /[぀-鿿가-힯]/.test(text);

  // 最後の句読点を探し、その後の部分だけを表示
  const boundaryRegex = isJapanese ? /[。！？]/g : /[.!?][ \n]/g;
  let lastBoundaryEnd = -1;
  let m;
  const re = new RegExp(boundaryRegex.source, 'g');
  while ((m = re.exec(text)) !== null) {
    lastBoundaryEnd = m.index + m[0].length;
  }

  if (lastBoundaryEnd > 0 && lastBoundaryEnd < text.length) {
    const after = text.slice(lastBoundaryEnd).trim();
    if (after.length > 0) {
      return after.length <= maxChars ? after : after.slice(-maxChars);
    }
  }

  if (text.length <= maxChars) return text;
  const tail = text.slice(-maxChars);
  if (natural) {
    // 先頭に半端な語が残らないよう、読点・カンマ→空白の順で切る位置を探す（先頭40%以内）
    const lim = Math.floor(tail.length * 0.4);
    const m = /[、，,]\s*/.exec(tail);
    if (m && m.index <= lim) return tail.slice(m.index + m[0].length);
    if (!isJapanese) { const sp = tail.indexOf(' '); if (sp > 0 && sp <= lim) return tail.slice(sp + 1); }
    return tail;
  }
  const spaceIdx = tail.indexOf(' ');
  return spaceIdx > 0 ? tail.slice(spaceIdx + 1) : tail;
}

export default function CaptionDisplay({ caption, fontSize, showOriginal, obsStyle, captionPosition = 'bottom', live = false, liveLines = 4 }: Props) {
  const obsClass = OBS_CLASS_MAP[obsStyle];
  const posClass = captionPosition === 'top' ? 'caption-pos-top'
    : captionPosition === 'center' ? 'caption-pos-center'
    : '';

  // 中間字幕（ストリーミング中）
  const [interimCaption, setInterimCaption] = useState<CaptionState | null>(null);

  // 確定字幕キュー表示
  const [displayItem, setDisplayItem] = useState<QueueItem | null>(null);
  const [fading, setFading] = useState(false);
  const [isShowingFinal, setIsShowingFinal] = useState(false);

  const queueRef = useRef<QueueItem[]>([]);
  const processingRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const processNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      processingRef.current = false;
      setFading(true);
      fadeTimerRef.current = setTimeout(() => {
        setIsShowingFinal(false);
        setDisplayItem(null);
        setFading(false);
      }, 400);
      return;
    }

    const item = queueRef.current.shift()!;
    processingRef.current = true;
    setFading(false);
    setDisplayItem(item);
    setIsShowingFinal(true);

    const duration = calcDisplayDuration(item.text);

    clearTimerRef.current = setTimeout(() => {
      setFading(true);
      fadeTimerRef.current = setTimeout(() => {
        setDisplayItem(null);
        setFading(false);
        fadeTimerRef.current = setTimeout(() => {
          processNext();
        }, 400);
      }, 800);
    }, Math.max(duration - 400, 500));
  }, []);

  useEffect(() => {
    if (caption === null) {
      queueRef.current = [];
      processingRef.current = false;
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      setInterimCaption(null);
      setDisplayItem(null);
      setIsShowingFinal(false);
      setFading(false);
      return;
    }

    if (live) {
      // 会議用：順番待ちをせず、最新の字幕をすぐ出す。しばらく何も来なければ消す
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      queueRef.current = [];
      processingRef.current = false;
      setFading(false);
      if (caption.interim) {
        setInterimCaption(caption);
        setIsShowingFinal(false);
      } else {
        const isJa = /[぀-鿿가-힯]/.test(caption.translation);
        setInterimCaption(null);
        setDisplayItem({ text: getLatestPortion(caption.translation, calcMaxChars(fontSize, isJa) * (liveLines === 2 ? 1 : 2), liveLines === 2), original: caption.original, direction: caption.direction, target: caption.target });
        setIsShowingFinal(true);
      }
      clearTimerRef.current = setTimeout(() => {
        setFading(true);
        fadeTimerRef.current = setTimeout(() => { setDisplayItem(null); setInterimCaption(null); setIsShowingFinal(false); setFading(false); }, 800);
      }, 15000);
      return;
    }

    if (caption.interim) {
      setInterimCaption(caption);
    } else {
      setInterimCaption(null);

      // 動的maxCharsで分割
      const isJapanese = /[぀-鿿가-힯]/.test(caption.translation);
      const maxChars = calcMaxChars(fontSize, isJapanese);
      const sentences = splitSentences(caption.translation, maxChars);
      const items: QueueItem[] = sentences.map(s => ({
        text: s,
        original: caption.original,
        direction: caption.direction,
        target: caption.target,
      }));

      queueRef.current.push(...items);

      if (!processingRef.current) {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        processNext();
      }
    }
  }, [caption, processNext, fontSize, live, liveLines]);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  // 表示テキスト決定
  const showInterim = !isShowingFinal && Boolean(interimCaption);
  const isJapaneseInterim = /[぀-鿿가-힯]/.test(interimCaption?.translation ?? '');
  const maxCharsInterim = calcMaxChars(fontSize, isJapaneseInterim);
  const interimText = getLatestPortion(interimCaption?.translation ?? '', live && liveLines === 4 ? maxCharsInterim * 2 : maxCharsInterim, live && liveLines === 2);
  const hasContent = showInterim ? Boolean(interimText) : Boolean(displayItem?.text);

  const currentDirection = showInterim
    ? (interimCaption?.direction ?? 'en-ja')
    : (displayItem?.direction ?? 'en-ja');

  const currentTarget = showInterim ? interimCaption?.target : displayItem?.target;

  const currentOriginal = showInterim
    ? (interimCaption?.original ?? '')
    : (displayItem?.original ?? '');

  const displayText = showInterim
    ? (interimText || '…')
    : (displayItem?.text || '…');

  return (
    <div
      className={`caption-screen ${obsClass} ${live ? 'caption-live' : ''} ${live && liveLines === 2 ? 'caption-live-2' : ''}`}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <div className={`caption-screen-wrap ${posClass}`}>
        <div className="caption-area">

          {showOriginal && hasContent && currentOriginal && (
            <div
              className="caption-original"
              style={{ fontSize: `${Math.round(fontSize * 0.5)}px` }}
            >
              {currentOriginal}
            </div>
          )}

          <div
            className={`caption-translation ${showInterim ? 'interim' : ''} ${
              !hasContent ? 'caption-empty' : fading ? 'fade-out' : 'fade-in'
            }`}
            style={{ fontSize: `${fontSize}px` }}
          >
            {hasContent ? displayText : ' '}
          </div>
        </div>
      </div>

      {hasContent && (
        <div className="caption-direction-indicator">
          {currentTarget ? `→ ${TARGET_LABELS[currentTarget] ?? currentTarget}` : DIRECTION_LABELS[currentDirection].label}
        </div>
      )}
    </div>
  );
}
